/**
 * WorkoutList - List view of workouts grouped by exercise
 * Shows comparison with previous workout and streaks
 */

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Dumbbell, Edit2, Trash2, TrendingUp, TrendingDown, Minus, Flame } from 'lucide-react';
import { useFitnessStore, EXERCISE_TYPES, MUSCLE_GROUPS } from '../../stores/fitnessStore';
import { fitness } from '../../services/supabaseService';

const WorkoutList = () => {
  const { workouts, selectWorkoutForEdit, deleteWorkout } = useFitnessStore();
  const [comparisonCache, setComparisonCache] = useState({});

  // Group workouts by date, then by exercise name
  const groupedByDate = useMemo(() => {
    const dateGroups = {};

    // First sort by row_order to maintain set order
    const sorted = [...workouts].sort((a, b) => (a.row_order || 0) - (b.row_order || 0));

    sorted.forEach((workout) => {
      const date = workout.date;
      if (!dateGroups[date]) {
        dateGroups[date] = {};
      }

      const exerciseName = workout.exercise_name || 'Diğer';
      if (!dateGroups[date][exerciseName]) {
        dateGroups[date][exerciseName] = {
          exerciseName,
          exerciseType: workout.exercise_type,
          muscleGroup: workout.muscle_group,
          sets: [],
        };
      }
      dateGroups[date][exerciseName].sets.push(workout);
    });

    return dateGroups;
  }, [workouts]);

  const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a));

  // Fetch comparison data for all exercises
  useEffect(() => {
    if (workouts.length === 0) return;

    const fetchComparisons = async () => {
      const newCache = {};

      for (const date of sortedDates) {
        const exerciseGroups = Object.values(groupedByDate[date]);

        for (const group of exerciseGroups) {
          const cacheKey = `${date}-${group.exerciseName}`;

          // Skip if already cached
          if (comparisonCache[cacheKey]) {
            newCache[cacheKey] = comparisonCache[cacheKey];
            continue;
          }

          try {
            const previousSets = await fitness.getPreviousWorkoutSets(
              group.exerciseName,
              date,
              null
            );

            if (previousSets.length > 0) {
              newCache[cacheKey] = {
                date: previousSets[0].date,
                sets: previousSets.map(s => ({
                  weight: parseFloat(s.weight) || 0,
                  reps: s.reps || 0,
                })),
              };
            }
          } catch (error) {
            console.error('Error fetching comparison:', error);
          }
        }
      }

      if (Object.keys(newCache).length > 0) {
        setComparisonCache(prev => ({ ...prev, ...newCache }));
      }
    };

    fetchComparisons();
  }, [workouts, sortedDates.join(',')]);

  if (workouts.length === 0) {
    return (
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 text-center">
        <Dumbbell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">
          Henüz antrenman yok
        </h3>
        <p className="text-gray-500">
          Bu ay için antrenman kaydı bulunmuyor.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedDates.map((date) => {
        const exerciseGroups = Object.values(groupedByDate[date]);

        return (
          <motion.div
            key={date}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden"
          >
            {/* Date header */}
            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-800">
              <h3 className="text-sm font-medium text-gray-300">
                {format(parseISO(date), 'd MMMM yyyy, EEEE', { locale: tr })}
              </h3>
            </div>

            {/* Exercise Groups */}
            <div className="divide-y divide-gray-800">
              {exerciseGroups.map((group) => {
                const cacheKey = `${date}-${group.exerciseName}`;
                const comparison = comparisonCache[cacheKey];

                return (
                  <ExerciseGroup
                    key={group.exerciseName}
                    group={group}
                    comparison={comparison}
                    onEdit={selectWorkoutForEdit}
                    onDelete={deleteWorkout}
                  />
                );
              })}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

const ExerciseGroup = ({ group, comparison, onEdit, onDelete }) => {
  const typeConfig = EXERCISE_TYPES[group.exerciseType] || EXERCISE_TYPES.other;
  const muscleConfig = MUSCLE_GROUPS[group.muscleGroup];

  // Calculate totals
  const totalSets = group.sets.length;
  const totalReps = group.sets.reduce((sum, s) => sum + (s.reps || 0), 0);
  const totalVolume = group.sets.reduce((sum, s) => sum + ((s.reps || 0) * parseFloat(s.weight || 0)), 0);

  // Calculate overall trend
  const getOverallTrend = () => {
    if (!comparison?.sets?.length) return null;

    let improvements = 0;
    let declines = 0;

    group.sets.forEach((set, idx) => {
      const prevSet = comparison.sets[idx];
      if (!prevSet) return;

      const currentWeight = parseFloat(set.weight) || 0;
      const prevWeight = prevSet.weight || 0;

      if (currentWeight > prevWeight) improvements++;
      else if (currentWeight < prevWeight) declines++;
    });

    if (improvements > declines) return 'up';
    if (declines > improvements) return 'down';
    return 'same';
  };

  const trend = getOverallTrend();

  return (
    <div className="py-3">
      {/* Exercise Header */}
      <div className="px-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`
            w-10 h-10 rounded-lg flex items-center justify-center text-lg
            ${getTypeBgColor(group.exerciseType)}
          `}>
            {typeConfig.emoji}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-white font-semibold">{group.exerciseName}</h4>
              {muscleConfig && (
                <span className="text-xs px-2 py-0.5 bg-gray-700 text-gray-400 rounded">
                  {muscleConfig.label}
                </span>
              )}
              {/* Overall trend badge */}
              {trend === 'up' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded">
                  <TrendingUp className="w-3 h-3" />
                </span>
              )}
              {trend === 'down' && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded">
                  <TrendingDown className="w-3 h-3" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              {group.exerciseType === 'cardio'
                ? <span>{group.sets.reduce((sum, w) => sum + (parseInt(w.duration_minutes) || 0), 0)} dk</span>
                : <span>{totalSets} set • {totalReps} tekrar</span>
              }
              {totalVolume > 0 && <span>• {totalVolume.toFixed(0)} kg</span>}
              {comparison && (
                <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-2xs">
                  vs {format(parseISO(comparison.date), 'd MMM', { locale: tr })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sets list */}
      <div className="space-y-1">
        {group.sets.map((workout, setIdx) => (
          <SetRow
            key={workout.id}
            workout={workout}
            setNumber={setIdx + 1}
            prevSet={comparison?.sets?.[setIdx]}
            onEdit={() => onEdit(workout)}
            onDelete={() => onDelete(workout.id)}
          />
        ))}
      </div>
    </div>
  );
};

const SetRow = ({ workout, setNumber, prevSet, onEdit, onDelete }) => {
  const isCardio = workout.exercise_type === 'cardio';

  // Clean up notes - remove "Set X" prefix
  const displayNotes = workout.notes && !workout.notes.match(/^Set \d+$/)
    ? workout.notes.replace(/^Set \d+ - /, '')
    : null;

  // Calculate comparison
  const getComparison = () => {
    if (!prevSet) return null;

    const currentWeight = parseFloat(workout.weight) || 0;
    const prevWeight = prevSet.weight || 0;

    if (currentWeight === 0) return null;

    const diff = currentWeight - prevWeight;

    if (diff > 0) return { type: 'up', diff };
    if (diff < 0) return { type: 'down', diff };
    return { type: 'same', diff: 0 };
  };

  const comparison = getComparison();

  const renderComparisonBadge = () => {
    if (!comparison) return null;

    const { type, diff } = comparison;

    if (type === 'up') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded">
          <TrendingUp className="w-3 h-3" />
          +{diff.toFixed(1)}
        </span>
      );
    }
    if (type === 'down') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs font-semibold rounded">
          <TrendingDown className="w-3 h-3" />
          {diff.toFixed(1)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
        <Minus className="w-3 h-3" />
      </span>
    );
  };

  return (
    <motion.div
      layout
      className="mx-2 px-3 py-2 rounded-lg bg-gray-800/30 hover:bg-gray-800/50 transition-colors group"
    >
      <div className="flex items-center gap-3">
        {/* Set number */}
        <div className="w-6 h-6 bg-gray-700 rounded-md flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-medium text-gray-400">{setNumber}</span>
        </div>

        {/* Set data */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            {isCardio ? (
              <>
                <span className="font-medium text-white">{workout.duration_minutes || '-'}</span>
                <span className="text-gray-500">dk</span>
                {workout.distance_km && (
                  <>
                    <span className="text-gray-600">•</span>
                    <span className="font-medium text-orange-400">
                      {parseFloat(workout.distance_km).toFixed(1)}
                    </span>
                    <span className="text-gray-500">km</span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="font-medium text-white">{workout.reps || '-'}</span>
                <span className="text-gray-500">tekrar</span>
                <span className="text-gray-600">×</span>
                <span className="font-medium text-orange-400">
                  {workout.weight ? parseFloat(workout.weight).toFixed(1) : '-'}
                </span>
                <span className="text-gray-500">kg</span>
                {renderComparisonBadge()}
              </>
            )}
          </div>

          {/* Notes */}
          {displayNotes && (
            <p className="text-xs text-gray-500 mt-1 italic">{displayNotes}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-md transition-colors"
            title="Düzenle"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
            title="Sil"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

function getTypeBgColor(type) {
  const colors = {
    fitness: 'bg-blue-500/20',
    strength: 'bg-blue-500/20',
    cardio: 'bg-green-500/20',
    kickbox: 'bg-red-500/20',
    flexibility: 'bg-purple-500/20',
    sport: 'bg-orange-500/20',
    other: 'bg-gray-500/20',
  };
  return colors[type] || colors.other;
}

export default WorkoutList;
